import React, { useState } from 'react';
import { Popup } from 'semantic-ui-react';
import "./style.less";
import { DocType, Document } from '../../types/Document';
import moment from 'moment-timezone';
import LoadMore from '../../../utils/LoadMore';
import { userFacingDateOnlyFormat } from '../../../utils/timezone';
import { DOC_TYPE_ICON, docTypeFromFileName } from '../../utils/document';
import DeleteConfirmationModal from '../DeleteConfirmationModal';
import {
    FileIcon,
    OpenIcon,
    TrashIcon,
    ProfileIcon,
    ClockIcon,
} from '../icons';

interface Props {
    docs: Document[];
    loading: boolean;
    hasMore: boolean;
    setPageNo: React.Dispatch<React.SetStateAction<number>>;
    facilityTimezone: string;
    activeResidentId?: string;
    onDelete?: (doc: Document) => void | Promise<void | boolean>;
}

const DocName = ({ name }: { name: string }) => (
    <Popup
        content={name}
        position="top center"
        size="mini"
        inverted
        mouseEnterDelay={300}
        trigger={<div className="doc-name">{name}</div>}
    />
);

const getDocType = (doc: Document): DocType => {
    const source = (doc.attachedFiles && doc.attachedFiles[0] && doc.attachedFiles[0].attachedLink)
        || (doc.files && doc.files[0])
        || '';
    return docTypeFromFileName(source);
};

const DocumentList = ({ docs, loading, setPageNo, hasMore, facilityTimezone, activeResidentId, onDelete }: Props) => {
    const [pendingDeleteDoc, setPendingDeleteDoc] = useState<Document | null>(null);
    const [deleting, setDeleting] = useState(false);

    const handleConfirmDelete = async () => {
        if (!pendingDeleteDoc || !onDelete) return;
        try {
            setDeleting(true);
            await onDelete(pendingDeleteDoc);
        } finally {
            setDeleting(false);
            setPendingDeleteDoc(null);
        }
    };

    const formatDate = (value: number | string) => {
        if (!value) {
            return "-";
        }
        return moment.tz(value, facilityTimezone).format(userFacingDateOnlyFormat);
    };

    const getDocumentLink = (doc: Document) => doc.attachedFiles && doc.attachedFiles[0] && doc.attachedFiles[0].attachedLink;
    const getConnectedEntitiesForDocument = (doc: Document) => {
        const services = Array.from(
            new Set((doc.connectedEntities?.services || [])
                .map((service) => (service?.name || "").trim())
                .filter(Boolean))
        ).sort((a, b) => a.localeCompare(b));

        const assets = Array.from(
            new Set((doc.connectedEntities?.assets || [])
                .map((asset) => (asset?.AssetName || "").trim())
                .filter(Boolean))
        ).sort((a, b) => a.localeCompare(b));

        const groups = Array.from(
            new Set((doc.connectedEntities?.groups || [])
                .map((group) => (group?.Name || "").trim())
                .filter(Boolean))
        ).sort((a, b) => a.localeCompare(b));

        return {
            services,
            assets,
            groups,
        };
    };

    return (
        <div>
            <div className="docsContainer">
                {docs && docs.length > 0 ? (
                    <div className="doc-grid">
                        {docs.map((doc) => {
                            const docLink = getDocumentLink(doc);
                            const linkedEntities = getConnectedEntitiesForDocument(doc);
                            const connectionGroups = [
                                { key: 'service', label: 'Service', values: linkedEntities.services },
                                { key: 'asset', label: 'Asset', values: linkedEntities.assets },
                                { key: 'group', label: 'Group', values: linkedEntities.groups },
                            ].filter((item) => item.values.length > 0);
                            const docType = getDocType(doc);
                            const TypeIcon = DOC_TYPE_ICON[docType];
                            const canDelete = Boolean(doc?.UploadedByResidentId && doc.UploadedByResidentId === activeResidentId && onDelete);
                            const createdBy = doc?.UploadedByResidentName || doc?.AddedByName || "-";

                            return (
                                <div className="doc-card" key={doc._id}>
                                    <div className="doc-card-top">
                                        <div className={`doc-icon-wrap ${docType}`}>
                                            <TypeIcon />
                                        </div>
                                        {canDelete && (
                                            <button
                                                type="button"
                                                className="doc-delete-btn"
                                                aria-label="Delete document"
                                                onClick={() => setPendingDeleteDoc(doc)}
                                            >
                                                <TrashIcon />
                                            </button>
                                        )}
                                    </div>
                                    <div className="doc-body">
                                        <DocName name={doc.Name} />
                                        <div className="doc-desc">{doc.Description || "No description added"}</div>
                                        {connectionGroups.length > 0 && (
                                            <div className="doc-connections">
                                                {connectionGroups.map((item) => (
                                                    <div className={`doc-connection ${item.key}`} key={item.key}>
                                                        <span className="conn-label">{item.label}</span>
                                                        <span className="conn-name">{item.values.join(', ')}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                        <div className="doc-meta">
                                            <ProfileIcon size={12} />
                                            <span className="doc-meta-item">{createdBy}</span>
                                            <span className="doc-meta-sep">·</span>
                                            <ClockIcon size={12} />
                                            <span className="doc-meta-item">{formatDate(doc.DateAdded)}</span>
                                        </div>
                                    </div>
                                    {docLink ? (
                                        <a className="doc-open-btn" href={docLink} target="_blank" rel="noopener noreferrer">
                                            <OpenIcon /> Open Document
                                        </a>
                                    ) : (
                                        <button type="button" className="doc-open-btn" disabled>
                                            <OpenIcon /> No File Attached
                                        </button>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                ) : !loading && (
                    <div className="doc-empty-state">
                        <div className="doc-empty-icon"><FileIcon size={48} strokeWidth={1.4} /></div>
                        <div className="doc-empty-title">No documents found</div>
                        <div className="doc-empty-text">There are no documents to show here yet.</div>
                    </div>
                )}
                <LoadMore isLoading={loading} hasMore={hasMore} next={() => { setPageNo((prev) => prev + 1); }} />
            </div>

            <DeleteConfirmationModal
                open={!!pendingDeleteDoc}
                onClose={() => { if (!deleting) setPendingDeleteDoc(null); }}
                onConfirm={handleConfirmDelete}
                messageToDisplay={`Are you sure you want to delete "${pendingDeleteDoc?.Name}"? This cannot be undone.`}
                loading={deleting}
            />
        </div>
    );
};

export default DocumentList;
